import { Combobox } from '@headlessui/react'
import { useState, useMemo, useCallback, useRef } from 'react'
import { debounce } from 'lodash'
import { Search, Plus } from 'iconoir-react'
import SecondaryButton from '../Buttons/SecondaryButton'
import { ChevronDownIcon } from '@heroicons/react/solid'

const GenericCombobox = ({
    onChange,
    label,
    icon,
    placeholder,
    options = [],
    isLoading = false,
    value = null,
    onInputChange,
    isClearable = false,
    isDisabled = false,
    addOption = null,
    error = false,
    addIcon = <Plus />,
    showAllOptions = false,
    showLabel = true,
    isSearchable = true,
}) => {
    const [query, setQuery] = useState('')
    const [isOpen, setIsOpen] = useState(false)
    const inputRef = useRef(null)

    const debouncedQuery = useMemo(
        () =>
            debounce((searchTerm) => {
                setQuery(searchTerm)
                if (onInputChange) {
                    onInputChange(searchTerm)
                }
            }, 200),
        [onInputChange]
    )

    const handleInputChange = (event) => {
        const searchTerm = event.target.value
        if (isSearchable && !showAllOptions) {
            debouncedQuery(searchTerm)
        }
    }

    const handleInputInteraction = useCallback(() => {
        setIsOpen(true)
        if (!showAllOptions && inputRef.current && isSearchable) {
            setTimeout(() => {
                inputRef.current?.select()
            }, 0)
        }
    }, [showAllOptions, isSearchable])

    const filteredOptions = (showAllOptions ? options : options || [])
        .filter(option => option !== null && option !== undefined)
        .filter(option => {
            if (!isSearchable || !query || showAllOptions) {
                return true
            }
            
            // Pour les practitioners : chercher dans label OU dans firstname + lastname
            const searchText = query.toLowerCase()
            
            // Cas 1: établissement avec label
            if (option.label) {
                if (option.label.toLowerCase().includes(searchText)) {
                    return true
                }
            }
            
            // Cas 2: docteur avec firstname + lastname
            if (option.firstname || option.lastname) {
                const fullName = `${option.firstname || ''} ${option.lastname || ''}`.trim().toLowerCase()
                if (fullName.includes(searchText)) {
                    return true
                }
            }
            
            return false
        })

    // Fonction pour rendre le contenu d'une option avec une icône
    const renderOptionContent = (option) => {
        // Déterminer le texte à afficher : label pour établissement, nom prénom pour docteur
        let displayText = option.label
        if (!displayText && (option.firstname || option.lastname)) {
            displayText = `${option.firstname || ''} ${option.lastname || ''}`.trim()
        }
        
        return option.icon ? (
            <div className="flex items-center space-x-2">
                <div>{option.icon}</div>
                <div>{displayText}</div>
            </div>
        ) : (
            displayText
        )
    }

    return (
        <div
            className={`relative w-full ${
                addOption && !isDisabled ? 'pr-14 lg:pr-14' : ''
            }`}
        >
            <div className="flex items-center space-x-2 pb-2">
                {icon && (
                    <i
                        className={`fa ml-2 ${icon} min-w-icon text-2xl lg:text-lg`}
                    />
                )}
                {showLabel && label && (
                    <label className="block text-base font-bold uppercase tracking-wide text-gray-700 lg:text-xs">
                        {label}
                    </label>
                )}
            </div>
            <Combobox
                value={value}
                onChange={onChange}
                disabled={isDisabled}
                by="value"
            >
                {({ open }) => (
                    <div className="relative w-full">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                            <Combobox.Input
                                ref={inputRef}
                                onChange={handleInputChange}
                                onClick={handleInputInteraction}
                                onFocus={handleInputInteraction}
                                displayValue={(option) => {
                                    if (!option) return ''
                                    // Afficher le label pour établissement, nom prénom pour docteur
                                    if (option.label) return option.label
                                    if (option.firstname || option.lastname) {
                                        return `${option.firstname || ''} ${option.lastname || ''}`.trim()
                                    }
                                    return ''
                                }}
                                placeholder={placeholder || label}
                                className={`w-full appearance-none rounded-lg border ${
                                    error ? 'border-red-500' : 'border-gray-200'
                                } h-10 px-10 text-sm shadow-sm placeholder:text-gray-400 focus:border-secondary-color focus:outline-none focus:ring-2 focus:ring-secondary-color/20 ${
                                    isDisabled
                                        ? 'cursor-not-allowed bg-gray-200'
                                        : 'bg-white'
                                }`}
                                readOnly={!isSearchable}
                            />
                            <Combobox.Button className="absolute inset-y-0 flex w-full items-center justify-end pr-2">
                                {isClearable && value && !isDisabled ? (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onChange(null)
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                    >
                                        ×
                                    </button>
                                ) : (
                                    <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                                )}
                            </Combobox.Button>
                        </div>
                        {open && (
                            <Combobox.Options
                                static
                                className="absolute z-10 mt-2 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5"
                            >
                                {isLoading ? (
                                    <div className="flex justify-center p-4">
                                        <div className="h-6 w-6 animate-spin rounded-full border-2 border-secondary-color border-t-transparent"></div>
                                    </div>
                                ) : filteredOptions.length > 0 ? (
                                    filteredOptions.map((option) => (
                                        <Combobox.Option
                                            key={option.value}
                                            value={option}
                                            disabled={option.isDisabled}
                                            className={({ active, disabled }) =>
                                                `cursor-pointer select-none px-4 py-3 text-sm transition-colors ${
                                                    active
                                                        ? 'bg-primary-color/10 text-primary-color'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                } ${
                                                    disabled
                                                        ? 'cursor-not-allowed opacity-50'
                                                        : ''
                                                }`
                                            }
                                        >
                                            {renderOptionContent(option)}
                                        </Combobox.Option>
                                    ))
                                ) : (
                                    <div className="px-4 py-3 text-sm text-gray-500">
                                        {isSearchable
                                            ? 'Tapez pour rechercher'
                                            : 'Aucun résultat trouvé'}
                                    </div>
                                )}
                            </Combobox.Options>
                        )}
                    </div>
                )}
            </Combobox>
            {addOption && !isDisabled && (
                <div className="absolute bottom-0 right-0 flex h-20 w-12 cursor-pointer items-center justify-center pl-2 pt-4 lg:pt-8">
                    <div className="pr-2 pt-2">
                        <SecondaryButton
                            title="Ajouter"
                            action={addOption}
                            icon={addIcon}
                        />
                    </div>
                </div>
            )}
        </div>
    )
}

export default GenericCombobox
